import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../common/ResponsiveTable";
interface Row {
  id: string;
  post_id: number;
  title: string;
  account: string;
  platform: string;
  metrics: Record<string, number>;
  checked_at: string | null;
  error?: string;
  availability: string;
}
interface Analytics {
  results: Row[];
  count: number;
  next: string | null;
  available_metrics: string[];
  reported_total: number | null;
  reported_destinations: number;
  total_destinations: number;
  top_posts: Row[];
}
export function SocialAnalytics() {
  const client = useQueryClient();
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    platform: "",
    metric: "likes",
  });
  const [page, setPage] = useState(1);
  const params = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v),
  );
  params.set("page", String(page));
  const query = useQuery({
    queryKey: ["social-analytics", filters, page],
    queryFn: async () =>
      (
        await api.get<Analytics>(
          `/api/marketing/social-posts/analytics/?${params}`,
        )
      ).data,
  });
  const refresh = useMutation({
    mutationFn: async () => {
      for (const id of new Set(query.data?.results.map((row) => row.post_id)))
        await api.get(`/api/marketing/social-posts/${id}/metrics/`);
    },
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["social-analytics"] }),
  });
  const update = (key: keyof typeof filters, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };
  const columns: ResponsiveColumn<Row>[] = [
    {
      key: "title",
      header: "Post",
      mobileVariant: "header",
      render: (row) => <Typography level="title-sm">{row.title}</Typography>,
    },
    {
      key: "account",
      header: "Destination",
      render: (row) => `${row.account} · ${row.platform.replaceAll("_", " ")}`,
    },
    {
      key: "metric",
      header: filters.metric.replaceAll("_", " "),
      render: (row) =>
        row.metrics[filters.metric] === undefined
          ? "Unavailable"
          : row.metrics[filters.metric].toLocaleString(),
    },
    {
      key: "checked",
      header: "Last checked",
      render: (row) =>
        row.checked_at
          ? new Date(row.checked_at).toLocaleString()
          : "Not recorded",
    },
    {
      key: "error",
      header: "Availability",
      mobileVariant: "body",
      render: (row) =>
        row.error ||
        (row.availability === "available"
          ? "Provider-reported metrics"
          : "Not collected or not reported by this provider"),
    },
  ];
  return (
    <Stack spacing={2}>
      <Typography level="h3">Social Media Analytics</Typography>
      <Typography level="body-sm">
        Compare provider-reported metrics for published posts. Missing metrics
        are shown as unavailable.
      </Typography>
      <Box
        sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "end" }}
      >
        <FormControl>
          <FormLabel>Published from</FormLabel>
          <Input
            type="date"
            value={filters.start_date}
            onChange={(e) => update("start_date", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Published through</FormLabel>
          <Input
            type="date"
            value={filters.end_date}
            onChange={(e) => update("end_date", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Platform</FormLabel>
          <Select
            value={filters.platform}
            onChange={(_, v) => update("platform", v || "")}
          >
            <Option value="">All platforms</Option>
            {["facebook", "instagram", "google_business_profile"].map((p) => (
              <Option key={p} value={p}>
                {p.replaceAll("_", " ")}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Metric</FormLabel>
          <Select
            value={filters.metric}
            onChange={(_, v) => update("metric", v || "likes")}
          >
            {[
              ...new Set([
                "likes",
                ...(query.data?.available_metrics || []),
                filters.metric,
              ]),
            ].map((metric) => (
              <Option key={metric} value={metric}>
                {metric.replaceAll("_", " ")}
              </Option>
            ))}
          </Select>
        </FormControl>
        <Button
          onClick={() => refresh.mutate()}
          loading={refresh.isPending}
          disabled={!query.data?.results.length}
        >
          Refresh visible posts
        </Button>
      </Box>
      {query.isPending && <Typography>Loading analytics…</Typography>}
      {query.isError && (
        <Alert
          color="danger"
          endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}
        >
          Unable to load analytics. Check the date range and try again.
        </Alert>
      )}
      {refresh.isError && (
        <Alert color="warning">
          Some metrics could not be refreshed. Retry to continue; previously
          collected data is retained.
        </Alert>
      )}
      {query.data && (
        <>
          <Card variant="soft">
            <Typography level="h2">
              {query.data.reported_total === null
                ? "Unavailable"
                : query.data.reported_total.toLocaleString()}
            </Typography>
            <Typography>
              {filters.metric.replaceAll("_", " ")} reported across{" "}
              {query.data.reported_destinations} of{" "}
              {query.data.total_destinations} destinations
            </Typography>
            <Typography level="body-xs">
              Totals include reported values only. Platforms define metrics
              differently; use the platform filter for closer comparisons.
            </Typography>
          </Card>
          {!!query.data.top_posts.length && (
            <Card variant="outlined">
              <Typography level="title-lg">
                Top content by {filters.metric.replaceAll("_", " ")}
              </Typography>
              {query.data.top_posts.map((row) => (
                <Typography key={row.id} level="body-sm">
                  {row.title} · {row.account}:{" "}
                  {row.metrics[filters.metric].toLocaleString()}
                </Typography>
              ))}
            </Card>
          )}
          <ResponsiveTable
            rows={query.data.results}
            getRowKey={(row) => row.id}
            columns={columns}
            emptyState={
              <Typography>No published posts match this view.</Typography>
            }
          />
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              disabled={page === 1 || query.isFetching}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Typography>
              Page {page} · {query.data.count} destinations
            </Typography>
            <Button
              disabled={!query.data.next || query.isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
}
